import React, { useState, useEffect, useCallback } from "react"
import styled, { keyframes } from "styled-components"
import { MobergFontFamily } from "../../Components/MobergFont/MobergFont"
import { MobergTheme } from "../../Components/MobergThemes/MobergColors"
import { MobergLogoSpinner } from "../../Components/MobergLogoSpinner/MobergLogoSpinner"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../Components/MobergButton/MobergButton"
import { MobergRow } from "../../Components/MobergLayout/MobergRow"
import { MobergColumn } from "../../Components/MobergLayout/MobergColumn"
import { Deployment, useEnvironmentVariablesProvider } from "../../Providers/EnvironmentVariablesProvider"
import { useEndpointProvider } from "../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../Providers/BackendLinksProvider"
import { MdSearch, MdCheckCircle, MdEdit, MdClose, MdCheck, MdOpenInNew, MdRefresh } from "react-icons/md"
import { useNavigate } from "react-router-dom"
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks"

function formatVarName(name) {
    return name
        .replace("FEATURE_FLAG_", "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
}

export default function FeatureFlags() {
    const [allVars, setAllVars] = useState({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [updating, setUpdating] = useState(null)
    const [envSearch, setEnvSearch] = useState("")
    const [toast, setToast] = useState(null)
    const [editingVar, setEditingVar] = useState(null)
    const [editValue, setEditValue] = useState("")
    const [needsReload, setNeedsReload] = useState(false)
    const { deployment } = useEnvironmentVariablesProvider()
    const endpointProvider = useEndpointProvider()
    const { LINKS } = useBackendLinksProvider()
    const navigate = useNavigate()

    const isDevEnvironment = !window.env.REACT_APP_DEPLOYMENT || window.env.REACT_APP_DEPLOYMENT === "DEVELOPMENT"

    function showToast(message) {
        setToast(message)
        setTimeout(() => setToast(null), 2500)
    }

    const fetchAll = useCallback(async () => {
        try {
            const data = await endpointProvider.post(LINKS.LOGIN.GET_WORKSPACE_ENV_DETAILS)
            setAllVars(data)
            setError(null)
        } catch (e) {
            setError(typeof e === "string" ? e : e.message)
        } finally {
            setLoading(false)
        }
    }, [endpointProvider, LINKS])

    useEffect(() => {
        fetchAll()
    }, [fetchAll])

    async function updateVar(variableName, newValue) {
        await endpointProvider.post(LINKS.LOGIN.SET_WORKSPACE_ENV, {
            variable_name: variableName,
            value: String(newValue),
        })
    }

    async function handleToggle(variableName, currentValue) {
        const newValue = !currentValue
        setAllVars(prev => ({ ...prev, [variableName]: { ...prev[variableName], value: newValue } }))
        setUpdating(variableName)

        try {
            await updateVar(variableName, newValue)
            showToast(`${formatVarName(variableName)} ${newValue ? "enabled" : "disabled"}`)
            if (variableName === "FEATURE_FLAG_DARK_MODE") {
                // EnvironmentVariablesProvider only fetches on mount, so the header's
                // theme toggle + MobergThemeProvider reconciliation need a reload to
                // pick up the new flag value. User-controlled via banner.
                setNeedsReload(true)
            }
        } catch (e) {
            setAllVars(prev => ({ ...prev, [variableName]: { ...prev[variableName], value: currentValue } }))
            setError(typeof e === "string" ? e : e.message)
        } finally {
            setUpdating(null)
        }
    }

    async function handleBulkToggle(enableAll) {
        const boolsToUpdate = Object.entries(allVars).filter(([, details]) => details.data_type === "boolean" && details.value !== enableAll)
        if (boolsToUpdate.length === 0) return

        const previousVars = { ...allVars }
        const updatedVars = { ...allVars }
        boolsToUpdate.forEach(([name]) => { updatedVars[name] = { ...updatedVars[name], value: enableAll } })
        setAllVars(updatedVars)
        setUpdating("bulk")

        try {
            await Promise.all(boolsToUpdate.map(([name]) => updateVar(name, enableAll)))
            showToast(`${boolsToUpdate.length} variable${boolsToUpdate.length > 1 ? "s" : ""} ${enableAll ? "enabled" : "disabled"}`)
        } catch (e) {
            setAllVars(previousVars)
            setError(typeof e === "string" ? e : e.message)
        } finally {
            setUpdating(null)
        }
    }

    async function handleSaveEnvVar(variableName) {
        const previousVars = { ...allVars }
        setAllVars(prev => ({ ...prev, [variableName]: { ...prev[variableName], value: editValue } }))
        setUpdating(variableName)
        setEditingVar(null)

        try {
            await updateVar(variableName, editValue)
            showToast(`${variableName} updated`)
        } catch (e) {
            setAllVars(previousVars)
            setError(typeof e === "string" ? e : e.message)
        } finally {
            setUpdating(null)
        }
    }

    function setDeploymentMode(newMode) {
        if (newMode === deployment) return
        localStorage.setItem("DEPLOYMENT_MODE_OVERRIDE", newMode)
        window.location.reload()
    }

    const sortedVars = Object.entries(allVars).sort(([a], [b]) => a.localeCompare(b))
    const totalCount = sortedVars.length
    const booleanCount = sortedVars.filter(([, d]) => d.data_type === "boolean").length
    const enabledCount = sortedVars.filter(([, d]) => d.data_type === "boolean" && d.value === true).length

    const filteredVars = sortedVars.filter(([name, details]) => {
        const q = envSearch.toLowerCase()
        return name.toLowerCase().includes(q)
            || formatVarName(name).toLowerCase().includes(q)
            || (details.description || "").toLowerCase().includes(q)
    })

    if (loading) {
        return (
            <LoadingContainer>
                <MobergLogoSpinner size={80} />
            </LoadingContainer>
        )
    }

    return (
        <PageContainer>
            {toast && (
                <ToastBar>
                    <MdCheckCircle size={18} />
                    {toast}
                </ToastBar>
            )}

            {error && <ErrorBanner>{error}</ErrorBanner>}

            {needsReload && (
                <ReloadBanner>
                    <MdRefresh size={18} />
                    <ReloadText>Dark mode setting changed, reload to apply across the app.</ReloadText>
                    <MobergButton
                        onClick={() => window.location.reload()}
                        variant={MobergButtonVariant.FILLED}
                        theme={MobergTheme.BLUE}
                        shape={MobergButtonShape.RECTANGLE}
                    >
                        Reload
                    </MobergButton>
                </ReloadBanner>
            )}

            <TopBar>
                <SearchWrapper>
                    <MdSearch size={18} color="var(--text-secondary-color)" />
                    <SearchInput
                        type="text"
                        placeholder="Search variables..."
                        value={envSearch}
                        onChange={(e) => setEnvSearch(e.target.value)}
                    />
                </SearchWrapper>
                <MobergButton
                    onClick={() => navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN)}
                    variant={MobergButtonVariant.OUTLINE}
                    theme={MobergTheme.BLUE}
                    shape={MobergButtonShape.RECTANGLE}
                >
                    Back to Dashboard
                    <MdOpenInNew size={16} />
                </MobergButton>
            </TopBar>

            {/* Frontend Section */}
            <Section>
                <SectionHeader>
                    <SectionTitle>Frontend</SectionTitle>
                </SectionHeader>
                <SettingRow>
                    <MobergColumn gap="4px">
                        <SettingLabel>Deployment Mode</SettingLabel>
                        <SettingDescription>
                            {isDevEnvironment
                                ? "Switch between Clinical and Cloud mode. This will reload the page."
                                : "Set by the environment configuration."
                            }
                        </SettingDescription>
                    </MobergColumn>
                    <MobergRow gap="12px" verticalAlign="center" expand={false}>
                        {isDevEnvironment ? (
                            <SegmentedControl>
                                <SegmentedOption
                                    $active={deployment !== Deployment.CLINICAL}
                                    onClick={() => setDeploymentMode(Deployment.CLOUD)}
                                >
                                    Cloud
                                </SegmentedOption>
                                <SegmentedOption
                                    $active={deployment === Deployment.CLINICAL}
                                    onClick={() => setDeploymentMode(Deployment.CLINICAL)}
                                >
                                    Clinical
                                </SegmentedOption>
                            </SegmentedControl>
                        ) : (
                            <DeploymentBadge $mode={deployment}>
                                {deployment === Deployment.CLINICAL ? "Clinical" : "Cloud"}
                            </DeploymentBadge>
                        )}
                    </MobergRow>
                </SettingRow>
            </Section>

            {/* Environment Variables Section */}
            <Section style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                <SectionHeader>
                    <MobergColumn gap="2px">
                        <SectionTitle>Environment Variables</SectionTitle>
                        <SectionSubtitle>{totalCount} variables &middot; {enabledCount} of {booleanCount} flags enabled</SectionSubtitle>
                    </MobergColumn>
                    <MobergRow gap="10px" verticalAlign="center" expand={false}>
                        <MobergButton
                            onClick={() => handleBulkToggle(true)}
                            variant={MobergButtonVariant.OUTLINE}
                            theme={MobergTheme.BLUE}
                            shape={MobergButtonShape.RECTANGLE}
                            disabled={updating === "bulk" || enabledCount === booleanCount}
                        >
                            Enable All
                        </MobergButton>
                        <MobergButton
                            onClick={() => handleBulkToggle(false)}
                            variant={MobergButtonVariant.OUTLINE}
                            theme={MobergTheme.RED}
                            shape={MobergButtonShape.RECTANGLE}
                            disabled={updating === "bulk" || enabledCount === 0}
                        >
                            Disable All
                        </MobergButton>
                    </MobergRow>
                </SectionHeader>

                <DataTable style={{ flex: 1, minHeight: 0 }}>
                    <TableHeader>
                        <HeaderCell style={{ width: "25%" }}>Variable</HeaderCell>
                        <HeaderCell style={{ width: "30%" }}>Description</HeaderCell>
                        <HeaderCell style={{ width: "10%", textAlign: "center" }}>Type</HeaderCell>
                        <HeaderCell style={{ width: "35%" }}>Value</HeaderCell>
                    </TableHeader>
                    <TableBody>
                        {filteredVars.map(([name, details]) => (
                            <TableRow key={name}>
                                <TableCell style={{ width: "25%" }}>
                                    <LabelRow>
                                        {details.data_type === "boolean" && <ActiveDot $on={details.value === true} />}
                                        <CellLabel>{formatVarName(name)}</CellLabel>
                                    </LabelRow>
                                    <CellSub>{name}</CellSub>
                                </TableCell>
                                <TableCell style={{ width: "30%", paddingRight: "16px" }}>
                                    {details.description
                                        ? <CellDescription>{details.description}</CellDescription>
                                        : <CellDescriptionMuted>—</CellDescriptionMuted>
                                    }
                                </TableCell>
                                <TableCell style={{ width: "10%", justifyContent: "center", alignItems: "center" }}>
                                    <TypeBadge>{details.data_type}</TypeBadge>
                                </TableCell>
                                <ValueCell style={{ width: "35%" }}>
                                    {details.data_type === "boolean" ? (
                                        <MobergRow gap="12px" verticalAlign="center" expand={false}>
                                            <Toggle $checked={details.value === true} onClick={() => !updating && handleToggle(name, details.value)}>
                                                <ToggleKnob $checked={details.value === true} />
                                            </Toggle>
                                            <StatusBadge $enabled={details.value === true}>
                                                {details.value === true ? "true" : "false"}
                                            </StatusBadge>
                                        </MobergRow>
                                    ) : editingVar === name ? (
                                        <MobergRow gap="6px" verticalAlign="center">
                                            <EditInput
                                                type={details.data_type === "integer" || details.data_type === "float" ? "number" : "text"}
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") handleSaveEnvVar(name)
                                                    if (e.key === "Escape") setEditingVar(null)
                                                }}
                                                autoFocus
                                            />
                                            <MobergButton
                                                onClick={() => handleSaveEnvVar(name)}
                                                variant={MobergButtonVariant.FILLED}
                                                theme={MobergTheme.BLUE}
                                                shape={MobergButtonShape.SQUARE}
                                                disabled={updating !== null}
                                            >
                                                <MdCheck size={16} />
                                            </MobergButton>
                                            <MobergButton
                                                onClick={() => setEditingVar(null)}
                                                variant={MobergButtonVariant.CLEAR}
                                                theme={MobergTheme.RED}
                                                shape={MobergButtonShape.SQUARE}
                                            >
                                                <MdClose size={16} />
                                            </MobergButton>
                                        </MobergRow>
                                    ) : (
                                        <MobergRow gap="8px" verticalAlign="center" horizontalAlign="space-between">
                                            <ValueText>{String(details.value)}</ValueText>
                                            <MobergButton
                                                onClick={() => {
                                                    if (/(^|_)URL(_|$)/.test(name) && !window.confirm(`Editing ${name} can break the app. Continue?`)) return
                                                    setEditingVar(name); setEditValue(String(details.value))
                                                }}
                                                variant={MobergButtonVariant.CLEAR}
                                                shape={MobergButtonShape.SQUARE}
                                                theme={MobergTheme.BLUE}
                                            >
                                                <MdEdit size={16} />
                                            </MobergButton>
                                        </MobergRow>
                                    )}
                                </ValueCell>
                            </TableRow>
                        ))}
                        {filteredVars.length === 0 && (
                            <EmptyRow>{envSearch ? "No variables match your search." : "No environment variables found."}</EmptyRow>
                        )}
                    </TableBody>
                </DataTable>
            </Section>
        </PageContainer>
    )
}

const toastSlideIn = keyframes`
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`

const toastFadeOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`

const PageContainer = styled.div`
    padding: 24px 30px;
    height: calc(100vh - 60px);
    display: flex;
    flex-direction: column;
    gap: 24px;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
`

const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 60px);
`

const ToastBar = styled.div`
    position: fixed;
    top: 76px;
    right: 30px;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: var(--color-surface-panel);
    border: 1px solid var(--color-success-text);
    border-radius: 6px;
    box-shadow: var(--modal-box-shadow);
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    font-weight: 500;
    color: var(--color-success-text);
    animation: ${toastSlideIn} 0.2s ease-out, ${toastFadeOut} 0.3s ease-in 2.2s forwards;
`

const ErrorBanner = styled.div`
    padding: 10px 16px;
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    color: var(--color-danger-text);
    background: var(--color-danger-hover-bg);
    border-radius: 6px;
    border: 1px solid var(--color-danger-text);
`

const ReloadBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    background: var(--color-hover-blue);
    border: 1px solid var(--color-accent-interactive);
    border-radius: 6px;
    color: var(--color-text-primary);
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    position: sticky;
    top: 0;
    z-index: 10;

    & > svg {
        color: var(--color-accent-interactive);
        flex-shrink: 0;
    }
`

const ReloadText = styled.span`
    flex: 1;
    font-weight: 500;
`

const Section = styled.div`
    display: flex;
    flex-direction: column;
`

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid var(--color-border-default);
`

const SectionTitle = styled.h2`
    font-family: ${MobergFontFamily.HEADER};
    font-size: 14px;
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`

const SectionSubtitle = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 12px;
    color: var(--text-secondary-color);
    margin-top: 2px;
    display: block;
`

const TopBar = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
`

const SearchWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    background: var(--color-input-bg);
    transition: border-color 0.2s;
    flex: 1;

    &:focus-within {
        border-color: var(--color-accent-interactive);
    }
`

const SearchInput = styled.input`
    border: none;
    outline: none;
    font-size: 14px;
    font-family: ${MobergFontFamily.REGULAR};
    background: transparent;
    color: var(--color-text-primary);
    width: 100%;

    &::placeholder {
        color: var(--text-secondary-color);
    }
`

const SettingRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;

    & + & {
        border-top: 1px solid var(--color-border-light);
    }
`

const SettingLabel = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
`

const SettingDescription = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 12px;
    color: var(--text-secondary-color);
`

const SegmentedControl = styled.div`
    display: inline-flex;
    border: 2px solid var(--color-accent-interactive);
    border-radius: 4px;
    overflow: hidden;
`

const SegmentedOption = styled.button`
    background: ${({ $active }) => $active ? "var(--color-accent-interactive)" : "transparent"};
    color: ${({ $active }) => $active ? "white" : "var(--color-accent-interactive)"};
    border: none;
    padding: 8px 22px;
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;

    & + & {
        border-left: 2px solid var(--color-accent-interactive);
    }

    &:hover {
        background: ${({ $active }) => $active ? "var(--color-accent-interactive)" : "var(--color-hover-blue)"};
    }
`

const DeploymentBadge = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    padding: 4px 12px;
    border-radius: 6px;
    color: ${({ $mode }) => $mode === "CLINICAL" ? "var(--color-success-text)" : "var(--color-accent-interactive)"};
    background: ${({ $mode }) => $mode === "CLINICAL" ? "var(--color-success-hover-bg)" : "var(--color-hover-blue)"};
`

const DataTable = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
`

const TableHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid var(--color-border-default);
`

const HeaderCell = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary-color);
`

const TableBody = styled.div`
    flex: 1;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: var(--color-scrollbar-track);
    }
    &::-webkit-scrollbar-thumb {
        background: var(--color-scrollbar-thumb);
        border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: var(--text-secondary-color);
    }
`

const TableRow = styled.div`
    display: flex;
    align-items: center;
    padding: 12px 0;
    transition: background-color 0.15s;

    &:hover {
        background-color: var(--color-hover-neutral);
    }

    & + & {
        border-top: 1px solid var(--color-border-light);
    }
`

const TableCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const ValueCell = styled.div`
    display: flex;
    align-items: center;
    box-sizing: border-box;
    padding-right: 8px;
`

const CellLabel = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
`

const LabelRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const ActiveDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $on }) => $on ? "var(--color-success-text)" : "var(--color-border-default)"};
    box-shadow: ${({ $on }) => $on ? "0 0 0 2px var(--color-success-glow)" : "none"};
    flex-shrink: 0;
    margin-left: 6px;
`

const CellDescription = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 12px;
    color: var(--text-secondary-color);
    line-height: 1.4;
`

const CellDescriptionMuted = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 12px;
    color: var(--color-border-default);
`

const CellSub = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 10px;
    color: var(--color-text-version);
    margin-top: 2px;
`

const StatusBadge = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    padding: 3px 10px;
    border-radius: 12px;
    color: ${({ $enabled }) => $enabled ? "var(--color-success-text)" : "var(--text-secondary-color)"};
    background: ${({ $enabled }) => $enabled ? "var(--color-success-hover-bg)" : "var(--color-hover-neutral)"};
`

const TypeBadge = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    padding: 3px 10px;
    border-radius: 6px;
    color: var(--text-secondary-color);
    background: var(--color-hover-neutral);
    border: 1px solid var(--color-border-light);
`

const ValueText = styled.span`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    color: var(--color-text-primary);
    word-break: break-all;
`

const EditInput = styled.input`
    flex: 1;
    padding: 8px 12px;
    border: 1.5px solid var(--color-accent-interactive);
    border-radius: 6px;
    font-size: 13px;
    font-family: ${MobergFontFamily.REGULAR};
    background: var(--color-input-bg);
    color: var(--color-text-primary);
    outline: none;

    &:focus {
        box-shadow: 0 0 0 2px var(--color-hover-blue);
    }
`

const Toggle = styled.div`
    width: 44px;
    height: 24px;
    border-radius: 12px;
    background: ${({ $checked }) => $checked ? "var(--color-success-text)" : "var(--color-border-default)"};
    cursor: pointer;
    position: relative;
    transition: background 0.2s;
    flex-shrink: 0;

    &:hover {
        opacity: 0.85;
    }
`

const ToggleKnob = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    position: absolute;
    top: 3px;
    left: ${({ $checked }) => $checked ? "23px" : "3px"};
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

const EmptyRow = styled.div`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    color: var(--text-secondary-color);
    text-align: center;
    padding: 48px 20px;
`
